import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, Tag, Share2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import { sanitizeHtml } from '../utils/sanitize';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const [postRes, allPostsRes] = await Promise.all([
          axios.get(`${API}/blog/${slug}`),
          axios.get(`${API}/blog`)
        ]);
        setPost(postRes.data);
        
        const related = allPostsRes.data
          .filter(p => p.category === postRes.data.category && p.slug !== slug)
          .slice(0, 3);
        setRelatedPosts(related);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <div className="text-6xl mb-6">📝</div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Article non trouvé</h1>
          <p className="text-slate-600 mb-8 max-w-md">
            L'article que vous recherchez n'existe pas ou a été déplacé.
          </p>
          <Link to="/blog">
            <Button className="bg-[#1E3A5F] hover:bg-[#152C4A] gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour aux articles
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Generate SEO meta description from excerpt
  const metaDescription = post ? post.excerpt.substring(0, 160) : '';
  const metaTitle = post ? `${post.title} | Atlantique Spa Boutique` : 'Blog | Atlantique Spa Boutique';
  
  // Breadcrumbs for SEO
  const breadcrumbs = post ? [
    { name: 'Accueil', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: `/blog/${post.slug}` }
  ] : [];

  // Article JSON-LD for SEO
  const articleJsonLd = post ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "author": {
      "@type": "Organization",
      "name": "Atlantique Spa Boutique"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Atlantique Spa Boutique",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "datePublished": post.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${window.location.origin}/blog/${post.slug}`
    }
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-post-page">
      {/* SEO Meta Tags */}
      {post && (
        <>
          <SEO
            title={metaTitle}
            description={metaDescription}
            breadcrumbs={breadcrumbs}
          />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
        </>
      )}
      
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/blog">Blog</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="truncate max-w-[200px]">{post.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/blog" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour aux articles
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(post.created_at)}
            </span>
            <span className="bg-slate-100 px-2 py-1 rounded">
              {post.category}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {post.title}
          </h1>
          <p className="text-xl text-slate-600">
            {post.excerpt}
          </p>
        </header>

        {/* Featured Image */}
        {post.image && (
          <div className="rounded-xl overflow-hidden mb-8">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-[400px] object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-10">
          <div 
            className="prose prose-slate prose-lg max-w-none
              prose-headings:text-slate-900 prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-4 prose-li:text-slate-700
              prose-strong:text-slate-900
              prose-blockquote:border-l-4 prose-blockquote:border-[#F97316] prose-blockquote:bg-orange-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
          />
        </div>

        {/* CTA discret - Maillage interne */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-6 mt-8">
          <p className="text-center text-slate-600 text-sm mb-4">Découvrez nos produits et services</p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            <Link to="/spas" className="bg-white px-4 py-2 rounded-lg text-[#1E3A5F] hover:text-[#F97316] font-medium transition-colors border border-slate-200 hover:border-orange-200">
              Nos spas
            </Link>
            <Link to="/pieces-detachees-spa" className="bg-white px-4 py-2 rounded-lg text-[#1E3A5F] hover:text-[#F97316] font-medium transition-colors border border-slate-200 hover:border-orange-200">
              Pièces détachées
            </Link>
            <Link to="/entretien-spa" className="bg-white px-4 py-2 rounded-lg text-[#1E3A5F] hover:text-[#F97316] font-medium transition-colors border border-slate-200 hover:border-orange-200">
              Entretien spa
            </Link>
            <Link to="/accessoires-spa" className="bg-white px-4 py-2 rounded-lg text-[#1E3A5F] hover:text-[#F97316] font-medium transition-colors border border-slate-200 hover:border-orange-200">
              Accessoires
            </Link>
            <Link to="/pompes-a-chaleur-spa" className="bg-white px-4 py-2 rounded-lg text-[#1E3A5F] hover:text-[#F97316] font-medium transition-colors border border-slate-200 hover:border-orange-200">
              Pompes à chaleur
            </Link>
          </div>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex items-center gap-2 mt-6">
            <Tag className="w-4 h-4 text-slate-400" />
            {post.tags.map(tag => (
              <span key={tag} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="bg-slate-900 rounded-xl p-8 mt-12 text-center">
          <h3 className="text-xl font-semibold text-white mb-2">
            Un projet de spa ? Parlons-en !
          </h3>
          <p className="text-slate-300 mb-6">
            Notre équipe vous conseille gratuitement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button className="bg-orange-500 hover:bg-orange-600 gap-2">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <Link to="/devis">
              <Button variant="outline" className="border-white text-white hover:bg-slate-800">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Articles similaires</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((related) => (
                <Link key={related.id} to={`/blog/${related.slug}`}>
                  <Card className="card-hover border-slate-200 h-full">
                    <CardContent className="p-4">
                      <span className="text-xs text-slate-500">{related.category}</span>
                      <h3 className="font-semibold text-slate-900 mt-1 line-clamp-2">{related.title}</h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Schema.org Article */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.image,
          "datePublished": post.created_at,
          "author": {
            "@type": "Organization",
            "name": "Atlantique Spa Boutique"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Atlantique Spa Boutique"
          }
        })
      }} />
    </div>
  );
};

export default BlogPostPage;
